import { useLocation, useNavigate } from 'react-router-dom';

import { useIsPlatformAdmin } from '@maestra/core/hooks/useIsPlatformAdmin';
import { useAdminRole, type ModuloAdmin } from '@maestra/core/hooks/useAdminRole';
import { useAppDispatch, useAppSelector } from '@maestra/core/store/store';
import { authActions } from '@maestra/core/store/slices/auth';
import { PAYWALL_DISABLED } from '@maestra/core/constants/maestra';

// OS DESTINOS DA CONTA E DO ADMIN, e o estado do plano — a lógica do antigo `SystemMenu` e do
// `PlanTag`, sem o desenho.
//
// A parte de admin some para quem não é admin, mas isso é conveniência de navegação: quem barra o
// acesso de fato é o guard de rota, a RLS e a verificação nas edge functions.

export type ChaveDoDestino =
  | 'planos' | 'configuracoes' | 'suporte'
  | 'admin-dashboard' | 'admin-artistas' | 'admin-conhecimento' | 'admin-cupons' | 'admin-pass'
  | 'admin-usuarios' | 'admin-vendas' | 'admin-avaliacoes' | 'admin-push' | 'admin-acessos' | 'admin-modelos';

export interface Destino {
  chave: ChaveDoDestino;
  rotulo: string;
  caminho: string;
}

// "Planos" vem antes de Configurações: em /planos mora o painel da assinatura.
const DA_CONTA: Destino[] = [
  { chave: 'planos', rotulo: 'Planos', caminho: '/planos' },
  { chave: 'configuracoes', rotulo: 'Configurações', caminho: '/settings' },
  { chave: 'suporte', rotulo: 'Suporte', caminho: '/suporte' },
];

// Cada item carrega o módulo que ele exige; o menu mostra só o que a pessoa alcança.
const DO_ADMIN: (Destino & { modulo?: ModuloAdmin; somenteAdminPleno?: boolean })[] = [
  { chave: 'admin-dashboard', rotulo: 'Dashboard', caminho: '/admin/dashboard', modulo: 'dashboard' },
  { chave: 'admin-artistas', rotulo: 'Perfis de artistas', caminho: '/admin/artistas', modulo: 'artistas' },
  { chave: 'admin-conhecimento', rotulo: 'Base de Conhecimento', caminho: '/admin/knowledge-base', modulo: 'knowledge-base' },
  { chave: 'admin-cupons', rotulo: 'Cupons', caminho: '/admin/cupons', modulo: 'cupons' },
  { chave: 'admin-pass', rotulo: 'Pass Access', caminho: '/admin/pass-access', modulo: 'pass-access' },
  { chave: 'admin-usuarios', rotulo: 'Usuários', caminho: '/admin/usuarios', modulo: 'usuarios' },
  { chave: 'admin-vendas', rotulo: 'CRM de vendas', caminho: '/admin/vendas', modulo: 'vendas' },
  { chave: 'admin-avaliacoes', rotulo: 'Avaliações', caminho: '/admin/avaliacoes', modulo: 'avaliacoes' },
  { chave: 'admin-push', rotulo: 'Enviar push', caminho: '/admin/push', modulo: 'push' },
  // Conceder acesso é só de admin pleno: fosse concedível, quem recebesse poderia se dar tudo.
  { chave: 'admin-acessos', rotulo: 'Acessos', caminho: '/admin/acessos', somenteAdminPleno: true },
  { chave: 'admin-modelos', rotulo: 'Modelos da IA', caminho: '/admin/modelos', somenteAdminPleno: true },
];

export type EstadoDoPlano = 'pro' | 'pendente' | 'free' | null;

export const useMenuDaConta = () => {
  const isAdmin = useIsPlatformAdmin();
  const { ehAdminPleno, podeAcessar } = useAdminRole();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const status = useAppSelector((s) => s.subscription.status);
  const initialized = useAppSelector((s) => s.subscription.initialized);
  const gracePeriodEndsAt = useAppSelector((s) => s.subscription.gracePeriodEndsAt);
  const asaasSubscriptionId = useAppSelector((s) => s.subscription.asaasSubscriptionId);
  const email = useAppSelector((s) => s.auth.user?.email);
  const nome = useAppSelector((s) => (s.auth.user?.user_metadata?.full_name || s.auth.user?.user_metadata?.name) as string | undefined);

  // Sem resposta do servidor ainda, o status é 'none' por padrão: mostrar "FREE" e trocar para
  // "PRO" um segundo depois seria pior do que não mostrar nada.
  //
  // 'pending' sem subscription_id é resquício do pagamento único, não uma assinatura em andamento.
  const pendente =
    (status === 'pending' && !!asaasSubscriptionId) ||
    (status === 'overdue' && !!gracePeriodEndsAt && Date.now() < new Date(gracePeriodEndsAt).getTime());
  const plano: EstadoDoPlano = !initialized || PAYWALL_DISABLED
    ? null
    : status === 'active' ? 'pro' : pendente ? 'pendente' : 'free';

  const admin = isAdmin
    ? DO_ADMIN.filter((i) => (i.somenteAdminPleno ? ehAdminPleno : !!i.modulo && podeAcessar(i.modulo)))
    : [];

  const ativo = (caminho: string) => location.pathname.startsWith(caminho);

  return {
    nome,
    email,
    plano,
    daConta: DA_CONTA,
    doAdmin: admin.map(({ chave, rotulo, caminho }) => ({ chave, rotulo, caminho })),
    ativo,
    abrir: (caminho: string) => navigate(caminho),
    sair: async () => {
      await dispatch(authActions.signOut());
      navigate('/login', { replace: true });
    },
  };
};
